import React, { useCallback, useContext, useState } from 'react';
import { Typography } from 'antd';
import { FileError, FileRejection, useDropzone } from 'react-dropzone';
import './AppealFiles.scss';
import { File as FileModel } from '../../../model/File';
import { convertErrorCode, formatFileSize } from '../../../utils/formatter';
import { Context } from '../../../index';

interface AppealFilesProps {
  handler: any;
  removeHandler: any;
  maxFiles?: number;
  accepts?: string[];
  maxSize?: number;
  multiple: boolean;
}

const AppealFiles = (props: AppealFilesProps): JSX.Element => {
  const [loadedFiles, setLoadedFiles] = useState<FileModel[]>([]);
  const [rejected, setRejected] = useState<FileRejection[]>([]);
  const [maxFiles, setMaxFiles] = useState<number>(
    props.maxFiles ? props.maxFiles : 5
  );
  const { store } = useContext(Context);
  const dropMaxFiles = props.maxFiles ? props.maxFiles : 5;
  const maxSize = props.maxSize ? props.maxSize : 10485760 / 2;
  const accepts = props.accepts
    ? props.accepts
    : [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'application/pdf',
        'application/x-pdf',
      ];
  const validateCount = (
    file: File
  ): { code: string; message: string } | null => {
    if (maxFiles < 1) {
      const err: FileError = {
        code: 'too-many-files',
        message: `Максимум ${dropMaxFiles} файлов`,
      };
      const reject: FileRejection = { file, errors: [err] };
      setRejected((prev) => [...prev, reject]);
      return err;
    }
    setRejected([]);
    return null;
  };

  const processAccepted = (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    store.upload(formData).then((resp) => {
      if (resp?.iri) {
        props.handler(resp.iri);
        setLoadedFiles((prev) => [...prev, resp]);
        setMaxFiles((prev) => {
          if (prev > 0) {
            return prev - 1;
          }
          return prev;
        });
      }
    });
  };

  const processRejected = (file: FileRejection) => {
    file.errors.map((err, index) => {
      file.errors[index].message = convertErrorCode(
        err.code,
        err.message,
        accepts,
        maxSize,
        dropMaxFiles
      );
      setRejected((prev) => [...prev, file]);
    });
  };

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    acceptedFiles.forEach((file: File) => processAccepted(file));
    rejectedFiles.map((file: FileRejection) => processRejected(file));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: accepts,
    maxSize: maxSize,
    maxFiles: maxFiles,
    multiple: props.multiple,
    noKeyboard: true,
    validator: validateCount,
    onDrop,
  });

  const deleteHandler = (id: number): void => {
    store.deleteFile(id).then(() => {
      const removed = loadedFiles.find((el) => el.id === id);
      const loadedNew = loadedFiles.filter((el) => el.id !== id);
      setLoadedFiles(loadedNew);
      if (removed) {
        props.removeHandler(removed.iri);
        setMaxFiles((prev) => {
          if (prev < dropMaxFiles) {
            return prev + 1;
          }
          return prev;
        });
      }
    });
  };

  const buildRejectionErrors = () => {
    let errors: FileError[] = [];
    rejected.map((file) => {
      file.errors.map((err) => {
        const existsError = errors.find((e) => e.code === err.code);
        if (existsError === undefined) {
          errors = [...errors, err];
        }
      });
    });
    if (errors.length > 0) {
      setTimeout(() => setRejected([]), 5000);
      return (
        <ul>
          {errors.map((err) => {
            return <li key={err.code}>{err.message}</li>;
          })}
        </ul>
      );
    }
    return <></>;
  };

  return (
    <div className="problemReportFiles">
      <div className="problemReportInfo">
        <label>Прикрепить файл</label>
        <Typography.Text>
          До {maxFiles} файлов не более {formatFileSize(maxSize)} каждый
        </Typography.Text>
        <Typography.Text>
          При загрузке вложений возможна задержка отображения файлов, в
          зависимости от скорости вашего интернета.
        </Typography.Text>
      </div>
      <div className="dropzoneContainer">
        <div className="dropzoneLoader">
          <div {...getRootProps({ className: 'dropzone' })}>
            <input {...getInputProps()} />
            {isDragActive ? (
              <div>
                <div className="dropzoneLoader__icon" />
                Прикрепить файл
              </div>
            ) : (
              <div>
                <div className="dropzoneLoader__icon" />
                Прикрепить файл
              </div>
            )}
          </div>
          <span>Или перетащите его сюда</span>
        </div>
        {loadedFiles && (
          <div className="dropzoneFiles">
            <ul>
              {loadedFiles.map((item) => {
                return (
                  <li key={item.id}>
                    <span>{item.originalName} </span>
                    <div
                      className="dropzoneCloseButton"
                      onClick={() => deleteHandler(item.id)}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {rejected.length > 0 && (
          <div className="dropzoneError">{buildRejectionErrors()}</div>
        )}
      </div>
    </div>
  );
};

export default AppealFiles;
